import { cn } from '@shared/utils/utils';
import { Checkbox } from '@shared/components/ui/checkbox';
import { StepperInput } from '@shared/components/ui/StepperInput';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Product } from '../../Product/Product';
import type { ProductListItemProps } from '../types';

/**
 * Componente ProductListItem - Item individual de producto en la lista
 *
 * Representa un producto individual dentro del selector con:
 * - Checkbox para seleccionar/deseleccionar
 * - Información del producto (nombre, descripción, SKU)
 * - StepperInput para controlar cantidad
 * - Imagen opcional del producto
 * - Precio e información de stock
 */
const ProductListItem = React.memo<ProductListItemProps>(
  ({ product, onToggleSelect, onQuantityChange, maxQuantity = 999 }) => {
    // 🌍 Hook de traducción para namespace UI compartido
    const { t } = useTranslation('ui');

    // 🎯 Handlers optimizados con useCallback
    const handleToggleSelect = useCallback(() => {
      onToggleSelect(product.id);
    }, [product.id, onToggleSelect]);

    const handleQuantityChange = useCallback(
      (newQuantity: number) => {
        onQuantityChange(product.id, newQuantity);
      },
      [product.id, onQuantityChange],
    );

    return (
      <div
        className={cn(
          'flex items-center gap-4 border-b py-2 transition-all duration-200',
          'hover:bg-accent/50 hover:border-accent',
          product.isSelected ? 'bg-accent/20 border-accent ring-1 ring-accent/30' : '',
        )}
        data-testid={`product-item-${product.id}`}
      >
        {/* 🔲 Checkbox y Producto - Lado izquierdo */}
        <div className="flex items-center gap-3 flex-grow min-w-0 mx-2">
          <div className="flex-shrink-0">
            <Checkbox
              checked={product.isSelected}
              onCheckedChange={handleToggleSelect}
              data-testid={`product-checkbox-${product.id}`}
              aria-label={t('productSelector.selectProduct', { name: product.name })}
            />
          </div>
          <div className="flex-grow min-w-0">
            <Product
              id={product.id as number}
              name={product.name}
              src={product.image ?? ''}
              code={product.code ?? ''}
            />
          </div>
        </div>

        {/* 🔢 Control de cantidad - Lado derecho */}
        <div className="flex-shrink-0 w-40">
          <StepperInput
            value={product.quantity}
            onChange={handleQuantityChange}
            min={1}
            disabled={!product.isSelected}
            data-testid={`product-quantity-${product.id}`}
            className={cn('w-full', !product.isSelected && 'opacity-50')}
          />
        </div>
      </div>
    );
  },
);

ProductListItem.displayName = 'ProductListItem';

export default ProductListItem;
